/* =================================================================================
 * custom-global.js
 *
 * Site-wide accessibility patches that complement the base theme without modifying
 * vendor files. Loaded via <script defer> in layout/theme.liquid.
 *
 * Issues handled here:
 *
 *  - MT-07 / MT-08 / MT-10 / MT-12 / MT-17 / MT-21 (WCAG 2.1.1 Keyboard):
 *    macOS Safari ships with "Press Tab to highlight each item on a webpage"
 *    OFF by default, which excludes BOTH <a href> AND <button> elements from
 *    the keyboard Tab order unless they carry an explicit tabindex.
 *
 *    The base theme works around this in some places (menu items, grid
 *    switcher, ...) by hand-coding tabindex="0", but many sections
 *    (slideshow buttons, banner-gallery tiles, cart drawer CTAs, footer
 *    links, product cards, checkout button, ...) miss it.
 *
 *    Instead of patching every Liquid template, this script normalises ALL
 *    real interactive <a href> and <button> elements once on load and again
 *    whenever new nodes are inserted (Shopify section reloads, quick view,
 *    lazy loaded content, AJAX cart refresh, etc.).
 *
 *    Elements that explicitly opt-out via tabindex="-1", aria-hidden="true",
 *    or are inside an [inert] subtree, are left untouched.
 * ================================================================================= */

(function () {
  'use strict';

  /**
   * Decide whether an <a> should receive tabindex="0".
   * Returns true only for "real" interactive links the user is expected to reach
   * via Tab. We intentionally skip:
   *   - anchors without href (purely decorative or JS-controlled)
   *   - empty href / "#"-only anchors (likely toggles already wired elsewhere)
   *   - anchors that already have any tabindex set (respect author intent)
   *   - anchors inside an aria-hidden="true" subtree (hidden from AT)
   *   - anchors inside an [inert] subtree
   */
  function shouldEnableTab(a) {
    if (!(a instanceof HTMLAnchorElement)) return false;
    if (a.hasAttribute('tabindex')) return false;

    var href = a.getAttribute('href');
    if (!href) return false;
    if (href === '#' || href.trim() === '') return false;

    // Don't pull elements that are hidden from assistive tech into the tab order.
    if (a.closest('[aria-hidden="true"]')) return false;
    if (a.closest('[inert]')) return false;

    return true;
  }

  function normalizeAnchor(a) {
    if (shouldEnableTab(a)) {
      a.setAttribute('tabindex', '0');
    }
  }

  /**
   * Decide whether a <button> should receive tabindex="0".
   * Same Safari-default restriction as anchors: <button> is excluded from the
   * Tab order unless "Press Tab to highlight each item" is enabled.
   *
   * We skip:
   *   - buttons with any tabindex already set (respect author intent — e.g.
   *     swiper/slider buttons that use tabindex="-1" by design)
   *   - aria-hidden / inert subtrees (not exposed to AT, must not be tabbed)
   *
   * We intentionally do NOT skip [disabled] buttons — :enabled vs :disabled
   * is the browser's native concern, and disabled buttons are already removed
   * from the Tab order without needing tabindex.
   */
  function shouldEnableTabButton(btn) {
    if (!(btn instanceof HTMLButtonElement)) return false;
    if (btn.hasAttribute('tabindex')) return false;
    if (btn.closest('[aria-hidden="true"]')) return false;
    if (btn.closest('[inert]')) return false;
    return true;
  }

  function normalizeButton(btn) {
    if (shouldEnableTabButton(btn)) {
      btn.setAttribute('tabindex', '0');
    }
  }

  function normalizeWithin(root) {
    if (!root) return;
    if (root.nodeType !== 1) return;

    // Cover both the root itself and any descendants.
    if (root.tagName === 'A') normalizeAnchor(root);
    if (root.tagName === 'BUTTON') normalizeButton(root);

    if (!root.querySelectorAll) return;

    var anchors = root.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) normalizeAnchor(anchors[i]);

    var buttons = root.querySelectorAll('button');
    for (var j = 0; j < buttons.length; j++) normalizeButton(buttons[j]);
  }

  /* MT-19 a11y (WCAG 2.1.1 Keyboard / 4.1.2 Name, Role, Value):
     Sort by combobox controller.

     The native <select name="sort_by"> on collection pages is hidden via
     `.visually-hidden` and a `<button role="combobox">` + `<ul role="listbox">`
     pair is rendered in front of it (see snippets/facets.liquid). This file
     wires the keyboard + mouse interactions per the W3C ARIA combobox 1.2
     listbox pattern, and mirrors every change back to the hidden <select> so
     the existing FacetFiltersForm submit/change pipeline keeps working.

     Keyboard contract (matches the filter accordions for consistency):
       - Enter / Space on the button     → toggle listbox open/close
       - ArrowDown on the button (closed)→ open listbox + focus current option
       - Enter / Space on an option      → select option + close + focus button
       - Escape on listbox or option     → close + focus button
       - ArrowDown / ArrowUp in listbox  → move focus to next / previous option
       - Home / End in listbox           → jump to first / last option
       - Click outside the combobox      → close (without focus restoration) */
  function initSortByCombobox(combobox) {
    var button = combobox.querySelector('[data-sort-by-button]');
    var listbox = combobox.querySelector('[data-sort-by-listbox]');
    var select = combobox.querySelector('[data-sort-by-select]');
    var current = combobox.querySelector('[data-sort-by-current]');
    if (!button || !listbox || !select) return;

    // Safari macOS ships with "Press Tab to highlight each item on a webpage"
    // OFF, which excludes <button> from the default Tab order (same restriction
    // as <a href>). The native <select> we replaced was a form field and was
    // therefore Tab-focusable by default — to preserve parity we force the
    // combobox button into the Tab sequence with tabindex="0".
    if (!button.hasAttribute('tabindex')) {
      button.setAttribute('tabindex', '0');
    }

    var options = listbox.querySelectorAll('[role="option"]');

    function openListbox() {
      listbox.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      var selected = listbox.querySelector('[aria-selected="true"]') || options[0];
      if (selected) selected.focus();
    }

    function closeListbox(focusButton) {
      listbox.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (focusButton) button.focus();
    }

    function selectOption(option) {
      if (!option) return;
      var value = option.getAttribute('data-value');
      for (var i = 0; i < options.length; i++) {
        options[i].setAttribute('aria-selected', 'false');
      }
      option.setAttribute('aria-selected', 'true');
      if (select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (current) current.textContent = option.textContent.trim();
      closeListbox(true);
    }

    button.addEventListener('click', function (e) {
      e.preventDefault();
      if (listbox.hidden) openListbox(); else closeListbox(true);
    });

    button.addEventListener('keydown', function (e) {
      var k = e.key;
      var code = e.code;
      if (k === 'Enter' || k === ' ' || code === 'Space') {
        e.preventDefault();
        if (listbox.hidden) openListbox(); else closeListbox(true);
      } else if (k === 'ArrowDown' || k === 'Down') {
        e.preventDefault();
        openListbox();
      } else if (k === 'Escape') {
        if (!listbox.hidden) {
          e.preventDefault();
          closeListbox(true);
        }
      }
    });

    options.forEach(function (option, idx) {
      option.addEventListener('click', function (e) {
        e.preventDefault();
        selectOption(option);
      });
      option.addEventListener('keydown', function (e) {
        var k = e.key;
        var code = e.code;
        if (k === 'Enter' || k === ' ' || code === 'Space') {
          e.preventDefault();
          selectOption(option);
        } else if (k === 'Escape') {
          e.preventDefault();
          closeListbox(true);
        } else if (k === 'ArrowDown' || k === 'Down') {
          e.preventDefault();
          (options[idx + 1] || options[0]).focus();
        } else if (k === 'ArrowUp' || k === 'Up') {
          e.preventDefault();
          (options[idx - 1] || options[options.length - 1]).focus();
        } else if (k === 'Home') {
          e.preventDefault();
          options[0].focus();
        } else if (k === 'End') {
          e.preventDefault();
          options[options.length - 1].focus();
        } else if (k === 'Tab') {
          // Let Tab leave the combobox naturally, but close the listbox first
          // so the closed state matches the focus state.
          closeListbox(false);
        }
      });
    });

    // Click-outside closes without restoring focus.
    document.addEventListener('click', function (e) {
      if (!combobox.contains(e.target) && !listbox.hidden) {
        closeListbox(false);
      }
    });
  }

  function initAllSortByComboboxes(root) {
    if (!root || root.nodeType !== 1) return;
    var nodes = root.querySelectorAll
      ? root.querySelectorAll('[data-sort-by-combobox]')
      : [];
    for (var i = 0; i < nodes.length; i++) {
      var cb = nodes[i];
      if (cb.__mt19SortByInit) continue;
      cb.__mt19SortByInit = true;
      initSortByCombobox(cb);
    }
    // Also handle the case where `root` itself is a combobox container.
    if (root.matches && root.matches('[data-sort-by-combobox]') && !root.__mt19SortByInit) {
      root.__mt19SortByInit = true;
      initSortByCombobox(root);
    }
  }

  function init() {
    normalizeWithin(document.body);
    initAllSortByComboboxes(document.body);

    // Watch for new nodes (section reloads in the theme editor, AJAX cart,
    // quick view, predictive search results, etc.) and re-apply.
    if ('MutationObserver' in window) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var m = mutations[i];
          for (var j = 0; j < m.addedNodes.length; j++) {
            normalizeWithin(m.addedNodes[j]);
            initAllSortByComboboxes(m.addedNodes[j]);
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
